import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import QuickReportButton from "@/components/QuickReportButton";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MapPin, Phone, Envelope, Buildings, WhatsappLogo, Bank, TennisBall, Check, X, MapTrifold, Globe, List, MapPinLine } from "@phosphor-icons/react";
import { COUNTRIES, STATES_BY_COUNTRY, countryName } from "@/lib/locations";
import ClubsMap from "@/components/ClubsMap";

const INITIAL_FORM = { name: "", address: "", country: "MX", state: "", city: "", phone: "", email: "", courts_count: 1, whatsapp: "", clabe_bbva: "", instagram_handle: "", latitude: "", longitude: "", sport: "padel", court_sports: [] };

export default function Clubs() {
  const { user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"

  const canEdit = ["admin", "directivo"].includes(user?.role);
  const [courtsDialogOpen, setCourtsDialogOpen] = useState(false);
  const [courtsClub, setCourtsClub] = useState(null);
  const [courtNames, setCourtNames] = useState([]);
  const [newCourtName, setNewCourtName] = useState("");
  const [editingCourtIdx, setEditingCourtIdx] = useState(-1);
  const [editingCourtValue, setEditingCourtValue] = useState("");

  const loadClubs = () => {
    api.get("/clubs").then(({ data }) => setClubs(data)).catch(() => toast.error("Error al cargar clubes")).finally(() => setLoading(false));
  };

  useEffect(() => { loadClubs(); }, []);

  const openCreate = () => {
    setEditingClub(null);
    setForm(INITIAL_FORM);
    setDialogOpen(true);
  };

  const openEdit = (club) => {
    setEditingClub(club);
    setForm({
      name: club.name,
      address: club.address || "",
      country: club.country || "MX",
      state: club.state || "",
      city: club.city || "",
      phone: club.phone || "",
      email: club.email || "",
      courts_count: club.courts_count,
      whatsapp: club.whatsapp || "",
      clabe_bbva: club.clabe_bbva || "",
      instagram_handle: club.instagram_handle || "",
      latitude: club.latitude ?? "",
      longitude: club.longitude ?? "",
      sport: club.sport || "padel",
      court_sports: club.court_sports || [],
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        latitude: form.latitude === "" || form.latitude == null ? null : parseFloat(form.latitude),
        longitude: form.longitude === "" || form.longitude == null ? null : parseFloat(form.longitude),
      };
      if (editingClub) {
        await api.put(`/clubs/${editingClub.id}`, payload);
        toast.success("Club actualizado");
      } else {
        await api.post("/clubs", payload);
        toast.success("Club creado");
      }
      setDialogOpen(false);
      loadClubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este club?")) return;
    try {
      await api.delete(`/clubs/${id}`);
      toast.success("Club eliminado");
      loadClubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const handleLogoUpload = async (clubId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/clubs/${clubId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      loadClubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir logo");
    }
  };

  const openCourts = (club) => {
    setCourtsClub(club);
    const names = club.court_names && club.court_names.length > 0
      ? [...club.court_names]
      : Array.from({ length: club.courts_count || 0 }, (_, i) => `Cancha ${i + 1}`);
    setCourtNames(names);
    setNewCourtName("");
    setEditingCourtIdx(-1);
    setCourtsDialogOpen(true);
  };

  const saveCourts = async (updated) => {
    try {
      await api.put(`/admin/clubs/${courtsClub.id}/courts`, { court_names: updated });
      toast.success("Canchas actualizadas");
      setCourtNames(updated);
      loadClubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar canchas");
    }
  };

  const addCourt = async () => {
    const name = newCourtName.trim();
    if (!name) { toast.error("Nombre requerido"); return; }
    const updated = [...courtNames, name];
    await saveCourts(updated);
    setNewCourtName("");
  };

  const deleteCourt = async (idx) => {
    if (!window.confirm(`Eliminar "${courtNames[idx]}"?`)) return;
    const updated = courtNames.filter((_, i) => i !== idx);
    await saveCourts(updated);
  };

  const startEditCourt = (idx) => {
    setEditingCourtIdx(idx);
    setEditingCourtValue(courtNames[idx]);
  };

  const saveEditCourt = async () => {
    const name = editingCourtValue.trim();
    if (!name) { toast.error("Nombre requerido"); return; }
    const updated = courtNames.map((n, i) => (i === editingCourtIdx ? name : n));
    await saveCourts(updated);
    setEditingCourtIdx(-1);
    setEditingCourtValue("");
  };

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // Aggregate countries + states from current clubs
  const { countriesAvailable, statesAvailable } = useMemo(() => {
    const byCountry = {};
    clubs.forEach((c) => {
      const cc = (c.country || "MX").toUpperCase();
      const st = (c.state || "").trim();
      if (!byCountry[cc]) byCountry[cc] = new Set();
      if (st) byCountry[cc].add(st);
    });
    const cList = Object.entries(byCountry).map(([cc, states]) => ({ code: cc, count: clubs.filter(c => (c.country || "MX").toUpperCase() === cc).length, states: Array.from(states).sort() }));
    const states = filterCountry !== "all" ? (byCountry[filterCountry] ? Array.from(byCountry[filterCountry]).sort() : []) : [];
    return { countriesAvailable: cList.sort((a, b) => a.code.localeCompare(b.code)), statesAvailable: states };
  }, [clubs, filterCountry]);

  const filteredClubs = useMemo(() => {
    return clubs.filter((c) => {
      const cc = (c.country || "MX").toUpperCase();
      if (filterCountry !== "all" && cc !== filterCountry) return false;
      if (filterState !== "all" && (c.state || "") !== filterState) return false;
      return true;
    });
  }, [clubs, filterCountry, filterState]);

  // Group filtered clubs by country → state for sectioned display
  const groupedClubs = useMemo(() => {
    const groups = {};
    filteredClubs.forEach((c) => {
      const cc = (c.country || "MX").toUpperCase();
      const st = (c.state || "Sin estado").trim() || "Sin estado";
      groups[cc] = groups[cc] || {};
      groups[cc][st] = groups[cc][st] || [];
      groups[cc][st].push(c);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([cc, states]) => ({
        country: cc,
        states: Object.entries(states).sort(([a], [b]) => a.localeCompare(b)).map(([st, items]) => ({ state: st, clubs: items })),
      }));
  }, [filteredClubs]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clubs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">{canEdit ? "Gestion" : "Directorio"}</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Clubes</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-[#0A0A0A] border border-white/10" data-testid="view-mode-toggle">
            <button onClick={() => setViewMode("list")} className={`px-3 h-11 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${viewMode === "list" ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-[#CCFF00]"}`} data-testid="view-list-btn">
              <List size={14} /> Lista
            </button>
            <button onClick={() => setViewMode("map")} className={`px-3 h-11 text-xs font-semibold uppercase tracking-wider transition-colors flex items-center gap-1.5 ${viewMode === "map" ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-[#CCFF00]"}`} data-testid="view-map-btn">
              <MapPinLine size={14} /> Mapa
            </button>
          </div>
          {canEdit && (
            <Button onClick={openCreate} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs min-h-[44px]" data-testid="create-club-btn">
              <Plus size={16} className="mr-2" /> Nuevo Club
            </Button>
          )}
          <QuickReportButton section="clubs" variant="compact" />
        </div>
      </div>

      {viewMode === "map" && clubs.length > 0 ? (
        <ClubsMap clubs={filteredClubs} />
      ) : (
      <>
      {/* Filters */}
      {clubs.length > 0 && (
        <div className="card-tactical p-4 flex flex-wrap items-center gap-3" data-testid="clubs-filters">
          <Globe size={16} className="text-[#CCFF00]" />
          <div className="flex items-center gap-2 flex-1 min-w-[150px]">
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Pais</Label>
            <Select value={filterCountry} onValueChange={(v) => { setFilterCountry(v); setFilterState("all"); }}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 w-full sm:w-[180px] text-sm" data-testid="filter-country"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">Todos los paises</SelectItem>
                {countriesAvailable.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{countryName(c.code)} ({c.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[150px]">
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Estado</Label>
            <Select value={filterState} onValueChange={setFilterState} disabled={filterCountry === "all" || statesAvailable.length === 0}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10 w-full sm:w-[200px] text-sm disabled:opacity-50" data-testid="filter-state"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all">Todos</SelectItem>
                {statesAvailable.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(filterCountry !== "all" || filterState !== "all") && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterCountry("all"); setFilterState("all"); }} className="text-gray-400 hover:text-[#CCFF00] text-xs h-9" data-testid="clear-filters">
              <X size={12} className="mr-1" /> Limpiar
            </Button>
          )}
          <p className="text-xs text-gray-500 ml-auto">{filteredClubs.length} / {clubs.length} clubes</p>
        </div>
      )}

      {clubs.length === 0 ? (
        <div className="card-tactical p-12 text-center">
          <Buildings size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No hay clubes registrados</p>
          {canEdit && <p className="text-sm text-gray-600 mt-1">Crea el primer club para comenzar</p>}
        </div>
      ) : filteredClubs.length === 0 ? (
        <div className="card-tactical p-12 text-center">
          <MapPin size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Sin clubes para este filtro</p>
        </div>
      ) : (
        <div className="space-y-8">
          {groupedClubs.map((group) => (
            <div key={group.country} data-testid={`country-group-${group.country}`}>
              <div className="flex items-center gap-3 mb-4 pb-2 border-b border-white/5">
                <Globe size={18} className="text-[#CCFF00]" />
                <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-tight text-white">{countryName(group.country)}</h2>
                <span className="text-xs text-gray-500 font-['JetBrains_Mono']">{group.states.reduce((s, x) => s + x.clubs.length, 0)} clubes</span>
              </div>
              <div className="space-y-6">
                {group.states.map((stateGroup) => (
                  <div key={stateGroup.state} data-testid={`state-group-${stateGroup.state}`}>
                    <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3 flex items-center gap-2">
                      <MapPin size={12} /> {stateGroup.state}
                      <span className="text-[10px] text-gray-600 font-['JetBrains_Mono']">({stateGroup.clubs.length})</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {stateGroup.clubs.map((club) => (
            <div key={club.id} className="card-tactical p-6 group" data-testid={`club-card-${club.id}`}>
              <div className="flex items-start justify-between mb-4">
                {club.logo_path && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/clubs/${club.id}/logo`}
                    alt={`${club.name} logo`}
                    className="w-16 h-16 object-contain bg-white p-1.5 border border-white/10 mr-3 shrink-0"
                    data-testid={`club-logo-${club.id}`}
                  />
                )}
                <Link to={`/dashboard/clubes/${club.id}`} className="flex-1 hover:text-[#CCFF00] transition-colors" data-testid={`open-club-${club.id}`}>
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white">{club.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 font-['JetBrains_Mono']">{club.courts_count} {club.courts_count === 1 ? "cancha" : "canchas"} · ver plano →</p>
                  {(() => {
                    const sp = club.sport || "padel";
                    return (
                      <span className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-widest font-bold border px-1.5 py-0.5"
                        style={{
                          color: sp === "tenis" ? "#FFB800" : sp === "ambos" ? "#00C853" : "#CCFF00",
                          borderColor: sp === "tenis" ? "rgba(255,184,0,0.4)" : sp === "ambos" ? "rgba(0,200,83,0.4)" : "rgba(204,255,0,0.4)",
                          backgroundColor: sp === "tenis" ? "rgba(255,184,0,0.08)" : sp === "ambos" ? "rgba(0,200,83,0.08)" : "rgba(204,255,0,0.08)",
                        }}
                        data-testid={`club-sport-badge-${club.id}`}
                      >
                        {sp === "tenis" ? "🎾 Tenis" : sp === "ambos" ? "🎾 Pádel + Tenis" : "🎾 Pádel"}
                      </span>
                    );
                  })()}
                </Link>
                {canEdit && (
                  <div className="flex gap-1">
                    <label className="text-gray-400 hover:text-[#CCFF00] h-8 w-8 inline-flex items-center justify-center cursor-pointer" title="Subir logo del club" data-testid={`upload-logo-${club.id}`}>
                      <Plus size={16} />
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => handleLogoUpload(club.id, e.target.files[0])} />
                    </label>
                    <Button variant="ghost" size="icon" onClick={() => openCourts(club)} className="text-gray-400 hover:text-[#CCFF00] h-8 w-8" data-testid={`manage-courts-${club.id}`} title="Gestionar canchas">
                      <TennisBall size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(club)} className="text-gray-400 hover:text-[#CCFF00] h-8 w-8" data-testid={`edit-club-${club.id}`} title="Editar club">
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(club.id)} className="text-gray-400 hover:text-red-400 h-8 w-8" data-testid={`delete-club-${club.id}`} title="Eliminar club">
                      <Trash size={16} />
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2 text-sm text-gray-400">
                {(club.country || club.state) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px]" data-testid={`club-location-${club.id}`}>
                      <Globe size={10} className="mr-1" /> {countryName(club.country || "MX")}{club.state ? ` · ${club.state}` : ""}{club.city ? ` · ${club.city}` : ""}
                    </Badge>
                  </div>
                )}
                {club.address && <div className="flex items-center gap-2"><MapPin size={14} className="text-gray-600" />{club.address}</div>}
                {club.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-gray-600" />{club.phone}</div>}
                {club.email && <div className="flex items-center gap-2"><Envelope size={14} className="text-gray-600" />{club.email}</div>}
                {club.whatsapp && <div className="flex items-center gap-2"><WhatsappLogo size={14} className="text-green-500" /><a href={`https://wa.me/52${club.whatsapp}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#CCFF00]">{club.whatsapp}</a></div>}
                {club.clabe_bbva && <div className="flex items-center gap-2"><Bank size={14} className="text-blue-400" /><span className="font-['JetBrains_Mono'] text-xs">CLABE: {club.clabe_bbva}</span></div>}
              </div>
              <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between flex-wrap gap-2">
                <Link to={`/dashboard/clubes/${club.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#CCFF00] hover:text-[#B3E600] font-bold" data-testid={`view-plan-${club.id}`}>
                  <MapTrifold size={14} /> Ver plano y reservar
                </Link>
                <Link to={`/dashboard/clubes/${club.id}/ar`} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 uppercase tracking-wider font-bold" data-testid={`club-ar-link-${club.id}`} title="Ver club en realidad aumentada">
                  ⚡ Ver AR
                </Link>
                <Link to={`/c/${club.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] px-2 py-1 bg-[#CCFF00]/10 text-[#CCFF00] border border-[#CCFF00]/30 hover:bg-[#CCFF00]/20 uppercase tracking-wider font-bold" data-testid={`public-club-link-${club.id}`} title="Perfil publico compartible">
                  Perfil publico
                </Link>
                {club.latitude != null && club.longitude != null && (
                  <div className="flex items-center gap-1">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${club.latitude},${club.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] px-2 py-1 bg-[#4285F4]/15 text-[#4285F4] border border-[#4285F4]/30 hover:bg-[#4285F4]/25 uppercase tracking-wider font-bold"
                      data-testid={`gmaps-link-${club.id}`}
                      title="Abrir en Google Maps"
                    >
                      Maps
                    </a>
                    <a
                      href={`https://waze.com/ul?ll=${club.latitude},${club.longitude}&navigate=yes`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] px-2 py-1 bg-cyan-400/15 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/25 uppercase tracking-wider font-bold"
                      data-testid={`waze-link-${club.id}`}
                      title="Abrir en Waze"
                    >
                      Waze
                    </a>
                  </div>
                )}
              </div>
            </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="club-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">{editingClub ? "Editar Club" : "Nuevo Club"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Nombre</Label>
              <Input data-testid="club-name-input" value={form.name} onChange={(e) => update("name", e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" required />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Direccion</Label>
              <Input data-testid="club-address-input" value={form.address} onChange={(e) => update("address", e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Pais</Label>
                <Select value={form.country} onValueChange={(v) => { update("country", v); update("state", ""); }}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="club-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Estado</Label>
                <Select value={form.state} onValueChange={(v) => update("state", v)}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="club-state-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                    {(STATES_BY_COUNTRY[form.country] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Ciudad</Label>
              <Input data-testid="club-city-input" value={form.city} onChange={(e) => update("city", e.target.value)}
                placeholder="Ej: San Mateo Atenco"
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Latitud <span className="text-gray-600 normal-case tracking-normal">(mapa)</span></Label>
                <Input data-testid="club-lat-input" type="number" step="0.000001" value={form.latitude} onChange={(e) => update("latitude", e.target.value)}
                  placeholder="19.2892"
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Longitud</Label>
                <Input data-testid="club-lng-input" type="number" step="0.000001" value={form.longitude} onChange={(e) => update("longitude", e.target.value)}
                  placeholder="-99.5387"
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600" />
              </div>
            </div>
            <p className="text-[11px] text-gray-500 -mt-2">Para obtener lat/lng: Google Maps → clic derecho en ubicacion → copia los numeros. Aparecera en el mapa interactivo.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Telefono</Label>
                <Input data-testid="club-phone-input" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Canchas</Label>
                <Input data-testid="club-courts-input" type="number" min="1" max="50"
                  value={form.courts_count ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") { update("courts_count", ""); return; }
                    const n = parseInt(v, 10);
                    if (!Number.isNaN(n) && n >= 0 && n <= 50) update("courts_count", n);
                  }}
                  onBlur={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (Number.isNaN(n) || n < 1) update("courts_count", 1);
                  }}
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Deporte del Club</Label>
              <Select value={form.sport || "padel"} onValueChange={(v) => update("sport", v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-11" data-testid="club-sport"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="padel">Pádel</SelectItem>
                  <SelectItem value="tenis">Tenis</SelectItem>
                  <SelectItem value="ambos">Pádel + Tenis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.sport === "ambos" && form.courts_count > 0 && (
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Tipo por cancha</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="club-court-sports">
                  {Array.from({ length: form.courts_count }, (_, i) => {
                    const cs = (form.court_sports || [])[i] || "padel";
                    return (
                      <div key={i} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 px-2 py-1">
                        <span className="text-[10px] uppercase tracking-widest text-gray-500 shrink-0">C{i + 1}</span>
                        <select
                          value={cs}
                          onChange={(e) => {
                            const arr = [...(form.court_sports || [])];
                            while (arr.length < form.courts_count) arr.push("padel");
                            arr[i] = e.target.value;
                            update("court_sports", arr.slice(0, form.courts_count));
                          }}
                          className="bg-transparent text-xs text-white flex-1 outline-none cursor-pointer"
                          data-testid={`club-court-sport-${i}`}
                        >
                          <option value="padel">Pádel</option>
                          <option value="tenis">Tenis</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Email</Label>
              <Input data-testid="club-email-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">WhatsApp</Label>
                <Input data-testid="club-whatsapp-input" value={form.whatsapp} onChange={(e) => update("whatsapp", e.target.value)}
                  placeholder="4641694920"
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">Instagram</Label>
                <Input data-testid="club-instagram-input" value={form.instagram_handle} onChange={(e) => update("instagram_handle", e.target.value)}
                  placeholder="@padelclub"
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] placeholder:text-gray-600" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-400 mb-2 block">CLABE BBVA (18 digitos)</Label>
              <Input data-testid="club-clabe-input" value={form.clabe_bbva} onChange={(e) => update("clabe_bbva", e.target.value)}
                placeholder="012180015254421972" maxLength={18}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00] font-['JetBrains_Mono'] placeholder:text-gray-600" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide w-full" data-testid="club-submit-btn">
                {editingClub ? "Actualizar" : "Crear Club"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={courtsDialogOpen} onOpenChange={setCourtsDialogOpen}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md" data-testid="courts-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">
              Canchas {courtsClub ? `- ${courtsClub.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                data-testid="new-court-name"
                placeholder="Nombre de cancha (ej. Cancha 4)"
                value={newCourtName}
                onChange={(e) => setNewCourtName(e.target.value)}
                className="bg-[#0A0A0A] border-white/20 text-white h-11 focus:border-[#CCFF00]"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCourt(); } }}
              />
              <Button type="button" onClick={addCourt} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="add-court-btn">
                <Plus size={16} className="mr-1" /> Agregar
              </Button>
            </div>
            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {courtNames.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">Sin canchas registradas</p>
              ) : (
                courtNames.map((name, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded p-2" data-testid={`court-row-${idx}`}>
                    <TennisBall size={16} className="text-[#CCFF00]" />
                    {editingCourtIdx === idx ? (
                      <>
                        <Input
                          value={editingCourtValue}
                          onChange={(e) => setEditingCourtValue(e.target.value)}
                          className="bg-[#1A1A1A] border-white/20 text-white h-9 flex-1"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditCourt(); } }}
                          data-testid={`edit-court-input-${idx}`}
                        />
                        <Button type="button" variant="ghost" size="icon" onClick={saveEditCourt} className="text-[#CCFF00] h-8 w-8" data-testid={`save-court-${idx}`}>
                          <Check size={16} />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => setEditingCourtIdx(-1)} className="text-gray-400 h-8 w-8">
                          <X size={16} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-white">{name}</span>
                        <Button type="button" variant="ghost" size="icon" onClick={() => startEditCourt(idx)} className="text-gray-400 hover:text-[#CCFF00] h-8 w-8" data-testid={`edit-court-${idx}`}>
                          <PencilSimple size={16} />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => deleteCourt(idx)} className="text-gray-400 hover:text-red-400 h-8 w-8" data-testid={`delete-court-${idx}`}>
                          <Trash size={16} />
                        </Button>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500 font-['JetBrains_Mono']">Los cambios se guardan automaticamente. Total: {courtNames.length}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
